import BetaTag from '@components/common/layout/BetaTag';
import DataGrid from '@components/common/DataGrid';
import DeactivateProductAccessModal from './DeactivateProductAccessModal';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EmptyState from '@components/common/EmptyState';
import ErrorPage from '@components/common/ErrorPage';
import FeaturesManagerButton from '@components/common/FeaturesManagerButton';
import Header from '@components/common/layout/Header';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Notifier from '@components/common/Notifier';
import PropTypes from 'prop-types';
import QuotaRow from '@components/common/quota/QuotaRow';
import QuotaUnitLabel from '@components/common/quota/QuotaUnitLabel';
import React, {useMemo, useState} from 'react';
import RecallWorkspacesModal from './RecallWorkspacesModal';
import ReportBar from '@components/provisioning/common/ReportBar';
import StatusCell from '@components/common/StatusCell';
import StatusChip from '@components/common/StatusChip';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import UndoIcon from '@mui/icons-material/Undo';
import routePath, {Routes} from '@account/utils/route';
import {ACCOUNT_MANAGER_URL} from '@account/config';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Unstable_Grid2 as Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {Label, LabelValue} from '@components/common/Label';
import {LineItemActivationStates} from '@account/constants/lineitems';
import {
  PA_BULK_LIMIT,
  deactivateProductAccess as _deactivateProductAccess,
  getProductAccesses,
} from '@account/api';
import {ProductAccessStates} from '@account/constants/products';
import {Link as RouterLink, useParams} from 'react-router-dom';
import {TODAY, getUTCDate, plainDateStamp} from '@account/utils/dates';
import {
  getActivationStateColor,
  getProductAccessStateColor,
  statusColorMappings,
} from '@account/utils/styles';
import {getError} from '@account/utils/errors';
import {
  getPrimaryQuota,
  normalizeSqmQuota,
  renderQuota,
} from '@account/utils/quota';
import {hasRoleLevel, roleLevels} from '@account/utils';
import {isTFOProduct as isTFOProductFn} from '@account/utils/plans';
import {useAuth} from '@apps-common/auth-react';
import {useFlags} from '@hooks/useFlags';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useProductAccess} from '@hooks/data/products/useProductAccess';
import {useProductAccessUsage} from '@hooks/data/products/useProductAccessUsage';

const LabelWithTooltip = ({children, tooltipText}) => (
  <Label sx={{display: 'flex', gap: 0.5}}>
    {children}
    <Tooltip title={tooltipText} placement="top">
      <InfoIcon color="disabled" fontSize="small" />
    </Tooltip>
  </Label>
);

LabelWithTooltip.propTypes = {
  children: PropTypes.node,
  tooltipText: PropTypes.string,
};

const DESCRIPTION_LINE_CLAMP = 2;

const DescriptionText = ({description}) => {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const el = ref.current;
    if (el) setClamped(el.scrollHeight > el.clientHeight);
  }, [description]);

  return (
    <Box sx={{mb: 3}}>
      <Typography
        ref={ref}
        variant="body2"
        color="text.secondary"
        sx={{
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: expanded ? 'unset' : DESCRIPTION_LINE_CLAMP,
          overflow: 'hidden',
        }}
      >
        {description}
      </Typography>
      {(clamped || expanded) && (
        <Link
          component="button"
          variant="body2"
          onClick={() => setExpanded(e => !e)}
          sx={{mt: 0.5}}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Link>
      )}
    </Box>
  );
};

DescriptionText.propTypes = {
  description: PropTypes.string.isRequired,
};

const CustomNoRowsOverlay = () => (
  <EmptyState
    icon={TravelExploreIcon}
    text="No line items"
    textSx={{maxWidth: '300px'}}
  />
);

const ProductDetailPage = () => {
  const auth = useAuth();
  const isPlanetAdmin = hasRoleLevel(auth, [roleLevels.planet_admin]);
  // Planeteers can view a product; the mutating actions below stay admin-only.
  const canViewProduct = hasRoleLevel(auth, [
    roleLevels.planet_user,
    roleLevels.planet_admin,
  ]);
  const {showCustomerAccounts, showAllAccounts, sqkmUsedConversionWorkaround} =
    useFlags();
  const {productAccessId} = useParams();
  const queryClient = useQueryClient();

  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isRecallOpen, setIsRecallOpen] = useState(false);
  const [kebabAnchorEl, setKebabAnchorEl] = useState(null);

  const {data, isLoading, error} = useProductAccess(productAccessId, {
    enabled: !!productAccessId && showCustomerAccounts,
  });

  const productAccess = data ?? {};
  const {organization_id: orgId, customer_account_id: customerAccountId} =
    productAccess;

  const {
    isLoading: usageLoading,
    error: usageError,
    data: usageData,
  } = useProductAccessUsage(productAccessId, {
    enabled: !!productAccessId && showCustomerAccounts,
  });

  const {data: workspacePasData, isLoading: workspacePasLoading} = useQuery(
    ['workspacePas', customerAccountId, productAccess.edp_id],
    () =>
      getProductAccesses({
        customerAccountId,
        search: productAccess.edp_id,
        limit: PA_BULK_LIMIT,
      }),
    {enabled: isPlanetAdmin && !!customerAccountId && isRecallOpen}
  );
  const workspacePas = (workspacePasData?.results ?? []).filter(
    pa => !!pa.workspace_id
  );
  const workspacePasTruncated =
    (workspacePasData?.meta?.count ?? 0) > PA_BULK_LIMIT;

  const handleDeactivateModalClose = () => {
    setIsDeactivateModalOpen(false);
  };

  const {
    mutate: deactivateProductAccess,
    isLoading: isLoadingDeactivationResponse,
    isError: isErrorDeactivating,
    error: errorDeactivation,
  } = useMutation(_deactivateProductAccess, {
    onSuccess: async () => {
      handleDeactivateModalClose();

      await queryClient.invalidateQueries(['productAccess']);
    },
  });

  const handleRemoveProduct = () => {
    deactivateProductAccess(Number(productAccessId));

    handleDeactivateModalClose();
  };

  const productTitle = productAccess?.title;

  const breadcrumbs = useMemo(
    () =>
      orgId
        ? [
            {label: 'All Organizations', url: Routes.ORGS},
            {
              label: productAccess?.organization_name ?? '...',
              url: routePath(Routes.ORG_DETAIL, {orgId: orgId}),
            },
            {label: productTitle},
          ]
        : customerAccountId
          ? [
              ...(showAllAccounts
                ? [
                    {label: 'All Accounts', url: Routes.ACCOUNTS},
                    {
                      label: productAccess?.customer_account_name ?? '...',
                      url: routePath(Routes.ACCOUNT_DETAIL, {
                        accountId: customerAccountId,
                      }),
                    },
                  ]
                : []),
              {label: productTitle},
            ]
          : [
              {label: 'All Organizations', url: Routes.ORGS},
              {label: productTitle},
            ],
    [
      customerAccountId,
      orgId,
      productAccess?.customer_account_name,
      productAccess?.organization_name,
      productTitle,
      showAllAccounts,
    ]
  );

  const getDaysUntilStartDateText = startDate => {
    const numDays = getUTCDate(startDate).startOf('day').diff(TODAY).as('days');

    return `Customer access starts in ${numDays} ${
      numDays === 1 ? 'day' : 'days'
    }`;
  };

  const columns = [
    {
      field: 'created_at',
      headerName: 'Transaction Date',
      valueGetter: params => plainDateStamp(params.value),
      flex: 0.2,
    },
    {
      field: 'quota_limit',
      headerName: 'Quota Limit',
      valueGetter: params => {
        if (params.row.has_unlimited_quota) return 'Unlimited';
        return renderQuota(params.row.quota_limit, params.row.quota_units);
      },
      renderCell: params => {
        return params.value === 'Error' ? (
          <Typography color="error" variant="body2">
            Error
          </Typography>
        ) : (
          params.value
        );
      },
      flex: 0.2,
    },
    {
      field: 'start_date',
      headerName: 'Access Dates',
      valueGetter: params => {
        const year = params.row.sfdc_year_label
          ? ` (${params.row.sfdc_year_label})`
          : '';

        return `${plainDateStamp(params.row.start_date)} to ${plainDateStamp(
          params.row.end_date
        )}${year}`;
      },
      flex: 0.4,
    },
    {
      field: 'state',
      headerName: 'Status',
      valueGetter: params => params.row.activation_state,
      renderCell: params => (
        <StatusCell
          status={params.value}
          sx={theme => ({
            color: getActivationStateColor(theme, params.value),
            fontWeight: 'inherit',
          })}
          tooltipText={
            params.value === LineItemActivationStates.PENDING
              ? getDaysUntilStartDateText(params.row.start_date)
              : null
          }
        />
      ),
      flex: 0.2,
    },
  ];

  if (!showCustomerAccounts || !canViewProduct) {
    return <ErrorPage statusCode={404} />;
  }

  if (error) {
    return <ErrorPage {...getError(error)} />;
  }

  if (isLoading) {
    return <Notifier progress open message="Loading product..." />;
  }

  if (!productAccess) {
    return null;
  }

  const productIsActive = productAccess.state === ProductAccessStates.ACTIVE;

  const canDeactivateProduct = isPlanetAdmin && productIsActive;

  const productAccessHasTileUnits = productAccess.quota_units === 'tile';
  const canRecall =
    isPlanetAdmin && !!customerAccountId && !productAccessHasTileUnits;
  const isTFOProduct = isTFOProductFn(productAccess);
  const supportsReservations = (productAccess.quota_style ?? '').startsWith(
    'billableaoi'
  );

  const showReserveQuotaButton =
    productIsActive && !isTFOProduct && supportsReservations;

  const showQuotaBar =
    productIsActive && !usageError && !usageLoading && !isTFOProduct;

  // Temporarily disabled until the reports backend supports customer accounts and product accesses
  // Original logic:
  // const showReports = isPlanetAdmin && productIsActive && !productAccessHasTileUnits;
  const showReports = false;

  const showDataAvailability =
    !!productAccess.toa_start_date || !!productAccess.toa_end_date;

  return (
    <Box sx={{paddingBottom: 8}}>
      <Header
        name={
          <>
            <span>{productTitle}</span>{' '}
            {productAccess.state === ProductAccessStates.DEACTIVATED && (
              <StatusChip
                label="Deactivated"
                statusColor={statusColorMappings.deactivated}
                style={{
                  marginLeft: 6,
                }}
              />
            )}
          </>
        }
        breadcrumbs={breadcrumbs}
        helmetTitle={productTitle}
      >
        <Stack gap={1} alignItems="center" flexDirection="row">
          {showReserveQuotaButton && (
            <FeaturesManagerButton
              orgId={orgId}
              showReserveQuotaCopy={hasRoleLevel(auth, [
                roleLevels.planet_user,
                roleLevels.planet_admin,
              ])}
            />
          )}

          {(canDeactivateProduct || canRecall) && (
            <>
              <IconButton
                aria-label="More options"
                size="small"
                onClick={e => setKebabAnchorEl(e.currentTarget)}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={kebabAnchorEl}
                open={!!kebabAnchorEl}
                onClose={() => setKebabAnchorEl(null)}
              >
                {canRecall && (
                  <MenuItem
                    onClick={() => {
                      setKebabAnchorEl(null);
                      setIsRecallOpen(true);
                    }}
                  >
                    <UndoIcon sx={{mr: 1, fontSize: '1.1rem'}} />
                    Recall quota from workspaces
                  </MenuItem>
                )}
                {canRecall && canDeactivateProduct && <Divider />}
                {canDeactivateProduct && (
                  <MenuItem
                    sx={theme => ({color: theme.palette.error.main})}
                    onClick={() => {
                      setKebabAnchorEl(null);
                      setIsDeactivateModalOpen(true);
                    }}
                  >
                    <DeleteIcon sx={{mr: 1}} />
                    Deactivate product
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Stack>
      </Header>

      {productAccess.description && (
        <DescriptionText description={productAccess.description} />
      )}

      <Grid container spacing={3}>
        <Grid xs={12} md={6}>
          <LabelWithTooltip tooltipText="Defined by provisioning completeness and access dates. Pending status has future access dates.">
            Status
          </LabelWithTooltip>

          <StatusCell
            status={productAccess.state}
            variant="body1"
            sx={theme => ({
              color: getProductAccessStateColor(theme, productAccess.state),
              fontWeight: 'inherit',
            })}
          />
        </Grid>

        {productAccess.pl_numbers && productAccess.pl_numbers.length > 0 && (
          <Grid xs={12} md={6}>
            <Label>
              PL {productAccess.pl_numbers.length === 1 ? 'Number' : 'Numbers'}
            </Label>
            <LabelValue>
              {(() => {
                const plNumbers = productAccess.pl_numbers;

                if (plNumbers.length <= 3) {
                  return plNumbers.join(', ');
                }

                // Show first 3 with tooltip for full list
                const displayText = `${plNumbers.slice(0, 3).join(', ')}, ...`;
                const tooltipText = plNumbers.join(', ');

                return (
                  <Tooltip title={tooltipText} placement="top">
                    <span>{displayText}</span>
                  </Tooltip>
                );
              })()}
            </LabelValue>
          </Grid>
        )}

        <Grid xs={12} md={6}>
          <LabelWithTooltip tooltipText="Time period in which user can access data.">
            Access Dates
          </LabelWithTooltip>

          <LabelValue>
            {plainDateStamp(productAccess.term_start_date)} to{' '}
            {plainDateStamp(productAccess.term_end_date)}
          </LabelValue>
        </Grid>

        {showDataAvailability && (
          <Grid xs={12} md={6}>
            <LabelWithTooltip tooltipText="Time extent for all available data">
              Data Availability
            </LabelWithTooltip>

            <LabelValue>
              {productAccess.toa_start_date
                ? plainDateStamp(productAccess.toa_start_date)
                : 'Start of term'}{' '}
              to{' '}
              {productAccess.toa_end_date
                ? plainDateStamp(productAccess.toa_end_date)
                : 'end of term'}
            </LabelValue>
          </Grid>
        )}

        {isPlanetAdmin && customerAccountId && (
          <Grid xs={12} md={6}>
            <Label sx={{margin: 0}}>Customer Account</Label>

            <LabelValue>
              {showAllAccounts ? (
                <Link
                  color="primary.dark"
                  component={RouterLink}
                  to={routePath(Routes.ACCOUNT_DETAIL, {
                    accountId: customerAccountId,
                  })}
                  underline="hover"
                >
                  {productAccess.customer_account_name ?? '...'}
                </Link>
              ) : (
                (productAccess.customer_account_name ?? '...')
              )}
            </LabelValue>
          </Grid>
        )}
      </Grid>

      {showQuotaBar && (
        <Stack sx={{gap: 1, marginTop: 3}}>
          <Typography variant="subtitle1" sx={{fontWeight: 700}}>
            Current Term Usage
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            Quota unit:{' '}
            <QuotaUnitLabel
              unit={
                usageData?.non_recurring?.[0]?.quota_unit ??
                productAccess.quota_units
              }
              variant="body2"
            />
          </Typography>
          {usageData?.non_recurring?.map(quota => {
            const {used, limit} = normalizeSqmQuota(
              {
                current: quota.used,
                limit: quota.quota_limit,
                units: quota.quota_unit,
              },
              sqkmUsedConversionWorkaround
            );
            return (
              <QuotaRow
                key={`${quota.quota_unit}-${quota.quota_limit}-${quota.start_date}`}
                quota={{limit, current: used, enabled: true}}
                quotaUnits={quota.quota_unit}
                subscriptionEnd={quota.end_date}
              />
            );
          })}
          {/**
            if there is no usage data fall back to the PA info, this will apply to reservation products
            this is fragile and should depend on `supports_reservations` but the API is not returning this bit.

            productAccess.unlimited_quota is an ANY-dimension rollup — it's true if *any*
            quota policy on the variant is unlimited (e.g. a genuinely unlimited `tile`
            streaming dimension), which can mask a finite primary/reservable quota (e.g.
            sqkm for reserved_area products like open water). getPrimaryQuota reads the
            per-dimension `quotas` array instead so only the primary dimension's own
            unlimited flag is used.
           */}
          {!usageData?.non_recurring?.length && (
            <QuotaRow
              quota={{
                ...getPrimaryQuota(productAccess),
                enabled: true,
              }}
              quotaUnits={productAccess.quota_units}
              subscriptionEnd={productAccess.term_end_date}
            />
          )}
        </Stack>
      )}

      {isTFOProduct && (
        <Grid sm={12} xl={12} sx={{marginTop: 3}}>
          <Card
            variant="outlined"
            sx={{
              backgroundColor: 'grey.100',
              borderColor: 'grey.300',
            }}
          >
            <CardContent>
              <Typography variant="subtitle1">
                This product consumes{' '}
                <Link
                  color="primary"
                  href="https://docs.planet.com/platform/processing-units"
                  target="_blank"
                  rel="noreferer noopener"
                >
                  processing units
                </Link>{' '}
                when you view basemap tiles. Please refer to your Processing
                Units{' '}
                <Link
                  color="primary"
                  href={`${ACCOUNT_MANAGER_URL}#/usage`}
                  target="_blank"
                  rel="noreferer noopener"
                >
                  usage
                </Link>{' '}
                to track consumption and understand how much has been used.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      )}

      {showReports && (
        <Stack sx={{gap: 1, marginTop: 5}}>
          <Stack direction="row" sx={{gap: 1}}>
            <Typography variant="subtitle1" sx={{fontWeight: 700}}>
              Reports
            </Typography>

            <BetaTag />
          </Stack>

          <Paper variant="outlined">
            <ReportBar
              cadences={['daily']}
              organization={{id: 324, name: 'placeholder'}}
              lineItemId={42}
              planId={42}
              productAccessId={Number(productAccessId)}
              reportType="pv_detailed"
              subscriptionStartDate={productAccess.term_start_date}
            />
          </Paper>
        </Stack>
      )}

      <Stack data-testid="product-history-section" sx={{gap: 1, marginTop: 5}}>
        <Typography variant="subtitle1" fontWeight={700}>
          Product History
        </Typography>

        <Box sx={{display: 'flex', flexDirection: 'column', minHeight: 120}}>
          <DataGrid
            autoHeight
            columns={columns}
            disableColumnFilter
            disableColumnSorting
            disableRowSelectionOnClick
            initialState={{
              sorting: {
                sortModel: [{field: 'created_at', sort: 'asc'}],
              },
            }}
            loading={isLoading}
            rowCount={(productAccess.line_items ?? []).length}
            rows={productAccess.line_items ?? []}
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
              noResultsOverlay: CustomNoRowsOverlay,
            }}
          />
        </Box>
      </Stack>

      {isRecallOpen && (
        <RecallWorkspacesModal
          open={isRecallOpen}
          onClose={() => setIsRecallOpen(false)}
          onSuccess={() => {
            queryClient.invalidateQueries(['productAccess', productAccessId]);
            queryClient.invalidateQueries([
              'productAccessTerms',
              productAccessId,
            ]);
          }}
          workspacePas={workspacePas}
          workspacePasLoading={workspacePasLoading}
          workspacePasTruncated={workspacePasTruncated}
        />
      )}

      {isDeactivateModalOpen && (
        <DeactivateProductAccessModal
          handleModalClose={handleDeactivateModalClose}
          handleDeactivate={handleRemoveProduct}
          isLoading={isLoadingDeactivationResponse}
          productTitle={productTitle}
        />
      )}

      <Notifier
        open={isLoadingDeactivationResponse}
        message="Deactivating product..."
        progress
      />

      <Notifier
        open={isErrorDeactivating}
        message={errorDeactivation?.message}
        severity="error"
        autoHideDuration={5000}
      />
    </Box>
  );
};

export default ProductDetailPage;
